import React, { useRef } from "react";
import { Button, useLocale } from "react-admin";
import { format } from "date-fns";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import {
  Typography,
  Popover,
  CircularProgress,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import {
  ProLogsLocale,
  dateFnsLocaleMap,
} from "../../../common/util/localization";
import { enUS } from "date-fns/locale";
import "./DatePager.css";
import { Variant } from "@material-ui/core/styles/createTypography";
import ProLogsDateFnsUtils from "../../../common/datetime/ProLogsDateFnsUtils";
import { AppState } from "../../../common/types";
import { useSelector } from "react-redux";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";

export enum ArrowsOrientation {
  Horizontal,
  Vertical,
}

const useStyles = makeStyles<Theme, Partial<Props>>((theme) => ({
  flex: (x) => ({
    flexDirection:
      x.arrowsOrientation === ArrowsOrientation.Horizontal
        ? "row"
        : "column-reverse",
  }),
}));

interface Props {
  value: Date;
  onSetCurrentDate: (date: Date) => void;
  loading?: boolean;
  previousEnabled?: boolean;
  nextEnabled?: boolean;
  hideArrows?: boolean;
  previousOnClick?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  nextOnClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  selectedDateFormat?: string;
  selectedDateVariant?: "inherit" | Variant;
  selectedDateColor?:
    | "inherit"
    | "textPrimary"
    | "initial"
    | "primary"
    | "secondary"
    | "textSecondary"
    | "error";
  firstDate?: string;
  arrowsOrientation?: ArrowsOrientation;
}

export const DatePager = ({
  value,
  onSetCurrentDate,
  previousEnabled = true,
  nextEnabled = true,
  previousOnClick,
  nextOnClick,
  loading,
  hideArrows,
  arrowsOrientation = ArrowsOrientation.Horizontal,
  selectedDateFormat = "MMM do, yyyy",
  selectedDateVariant = "body1",
  selectedDateColor = "textPrimary",
  firstDate,
}: Props) => {
  const datePagerRef = useRef<HTMLDivElement>(null);
  const [
    datePickerAnchorEl,
    setDatePickerAnchorEl,
  ] = React.useState<HTMLDivElement | null>(null);
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = !!dateFnsLocaleMap[locale]
    ? dateFnsLocaleMap[locale]
    : enUS;

  const datePickerOpen = !!datePickerAnchorEl;
  const classes = useStyles({ arrowsOrientation });
  const themeName = useSelector((x: AppState) => x.theme);

  return (
    <div
      ref={datePagerRef}
      className={`${classes.flex} date-pager ${
        themeName === "dark" ? "date-pager-dark" : ""
      }`}>
      {!hideArrows ? (
        <Button disabled={!previousEnabled} label="" onClick={previousOnClick}>
          {arrowsOrientation === ArrowsOrientation.Horizontal ? (
            <KeyboardArrowLeftIcon />
          ) : (
            <KeyboardArrowDown />
          )}
        </Button>
      ) : null}
      <div className="date-display">
        {loading ? (
          <CircularProgress size={16} />
        ) : (
          <Typography
            color={selectedDateColor}
            variant={selectedDateVariant}
            onClick={() => setDatePickerAnchorEl(datePagerRef.current)}>
            {format(value, selectedDateFormat, {
              locale: dateFnsLocale,
            })}
          </Typography>
        )}
      </div>
      {!hideArrows ? (
        <Button disabled={!nextEnabled} label="" onClick={nextOnClick}>
          {arrowsOrientation === ArrowsOrientation.Horizontal ? (
            <KeyboardArrowRightIcon />
          ) : (
            <KeyboardArrowUp />
          )}
        </Button>
      ) : null}
      <Popover
        open={datePickerOpen}
        anchorEl={datePickerAnchorEl}
        onClose={() => setDatePickerAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <MuiPickersUtilsProvider
          utils={ProLogsDateFnsUtils}
          locale={!!dateFnsLocaleMap[locale] ? dateFnsLocaleMap[locale] : enUS}>
          <DatePicker
            autoOk
            variant="static"
            openTo="date"
            maxDate={new Date()}
            value={value}
            minDate={firstDate ?? new Date("1900-01-01")}
            onChange={(date) => {
              if (!!date) {
                onSetCurrentDate(date);
              }

              setDatePickerAnchorEl(null);
            }}
          />
        </MuiPickersUtilsProvider>
      </Popover>
    </div>
  );
};
